import { PartUploadError, UploadCancelled } from './multipartUpload';

const messages: { [code: string]: string } = {
  VIDEO_PROBE_FAILED: 'The file was uploaded but could not be read as a video.',
  VIDEO_URL_IN_USE: 'The other camera already uses a file with this name. Rename the file and upload it again.',
  VIDEO_KEY_TAKEN: 'This session already has too many files with this name. Rename the file.',
  SIZE_MISMATCH: 'The uploaded size does not match the file. Upload it again.',
  PARTS_REJECTED: 'R2 rejected the uploaded parts. Upload the file again.',
  FILENAME_INVALID: 'Use a .mp4, .mkv, .ts, .avi or .mov file with a name of at most 200 characters.',
  SIZE_OUT_OF_RANGE: 'The file is empty or larger than the maximum upload size.',
  PARTS_INVALID: 'Not every part of the file was uploaded. Upload it again.',
  KEY_NOT_FOR_SESSION: 'This upload belongs to another session or the match name changed. Upload the file again.',
  UPLOAD_NOT_FOUND: 'The upload expired. Upload the file again.',
  UPLOAD_ID_EMPTY: 'The upload was not started. Upload the file again.',
  CONTENT_TYPE_INVALID: 'The browser reported an unsupported file type. Use a .mp4, .mkv, .ts, .avi or .mov file.',
  SESSION_ID_INVALID: 'Save the session before uploading videos.',
};

type ApiError = {
  message?: string;
  response?: {
    status: number;
    data?: { code?: string; failures?: Array<{ code?: string; message?: string }> };
  };
};

export default function uploadErrorMessage(error: unknown): string {
  if (error instanceof UploadCancelled) {
    return 'Upload cancelled.';
  }
  if (error instanceof PartUploadError) {
    return error.message;
  }

  const apiError = error as ApiError;
  const status = apiError?.response?.status;

  if (status === 401 || status === 403) {
    return 'You are not allowed to upload videos to this session.';
  }

  const failures = apiError?.response?.data?.failures || [];
  const known = failures
    .map((failure) => messages[failure.code || ''] || messages[failure.message || ''])
    .find((message) => !!message);

  if (known) {
    return known;
  }
  if (status === 404) {
    return 'The session or upload was not found. Reload the page and upload the file again.';
  }
  if (failures.length > 0) {
    return failures.map((failure) => failure.message || failure.code).join(', ');
  }

  return apiError?.message || 'The upload failed.';
}
